import React from "react";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import { KuikaCreateCard } from "../../../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaModalIconWrapper, {
  KuikaModalIconIntent
} from "../../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface IIntentSample {
  intent: KuikaModalIconIntent;
  iconName: string;
  caption: string;
}

const INTENTS: IIntentSample[] = [
  { intent: "create", iconName: "plus", caption: "create · new folder, name, token" },
  { intent: "edit", iconName: "edit", caption: "edit · rename, move" },
  { intent: "destructive", iconName: "trash", caption: "destructive · delete, revoke, discard" },
  { intent: "warning", iconName: "alert-triangle", caption: "warning · unsaved, overwrite" },
  { intent: "success", iconName: "check", caption: "success · done" },
  { intent: "neutral", iconName: "settings", caption: "neutral · hand over, pick" }
];

export const ModalIconWrapperStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Modal Icon Wrapper"
      componentName="KuikaModalIconWrapper"
      sourcePath="ui-kit/kuika-modal-icon-wrapper"
      description="Icon badge for short dialogs — confirmations and one- or two-field create/rename dialogs; long forms don't take one. The caller picks an intent, never a colour: it sets the glow, the tile's tint, its edge and the glyph, in the hue of the dialog's primary button. The glyph draws itself once per mount, after the modal's rise. Its inner plate is the kit's icon tile (64px, radius 16, 32px glyph); the glow adds --gap-l on every side with concentric corners."
    >
      <SpecRow label="Intents" caption="meaning → colour, matching the dialog's primary button">
        {INTENTS.map((sample: IIntentSample) => (
          <div key={sample.intent} className="lab-stack" style={{ alignItems: "center", gap: 6 }}>
            <KuikaModalIconWrapper iconName={sample.iconName} iconType="common" intent={sample.intent} />
            <span className="lab-caption">{sample.caption}</span>
          </div>
        ))}
      </SpecRow>
      <SpecRow
        label="Icon tile family"
        caption="one geometry (styles/base/_icon-tile.scss): md 48 · lg 64, radius = side / 4, 32px glyph — modal plate · empty state · create card · page empty state"
      >
        <KuikaModalIconWrapper iconName="settings" iconType="common" intent="neutral" />
        <div style={{ width: 240 }}>
          <KuikaEmptyState svgName="folder-check" name="folder" />
        </div>
        <div style={{ width: 300 }}>
          <KuikaCreateCard
            title="New folder"
            description="Start an empty folder."
            icon={<KuikaSvg name="plus" />}
            onClick={(): void => undefined}
          />
        </div>
        <div style={{ width: 300 }}>
          <KuikaEmptyState svgName="search" customName="No results" description="Try another filter." variant="page" />
        </div>
      </SpecRow>
      <SpecRow label="Custom size" caption="iconWidth / iconHeight override the tile's glyph size">
        <KuikaModalIconWrapper iconName="star" intent="edit" iconWidth={24} iconHeight={24} />
        <KuikaModalIconWrapper iconName="star" intent="edit" />
        <KuikaModalIconWrapper iconName="star" intent="edit" iconWidth={48} iconHeight={48} />
      </SpecRow>
    </ShowcaseFrame>
  );
};
